import { useQuery } from "@apollo/client";
import { IonCol, IonContent, IonGrid, IonPopover, IonRow } from "@ionic/react";
import { motion } from "framer-motion";
import { PropsWithChildren, ReactNode, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { FormattedMessage, useIntl } from "react-intl";

import AccountDropdown from "@components/account/AccountDropdown";
import CircleButton from "@components/buttons/CircleButton";
import MobileHeader from "@components/MobileHeader";
import Modal from "@components/Modal";
import NotificationCenter from "@components/NotificationCenter";
import NumberCircle from "@components/NumberCircle";
import AppPageWithMenu from "@components/pageTypes/AppPageWithMenu";
import styles from "@components/pageTypes/Page.module.css";
import Icon from "@components/text/Icon";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  GetJourneyForMessagesDocument,
  GetJourneyForMessagesQuery,
  GetJourneyForMessagesQueryVariables
} from "@typing/Generated";
import {
  modalSlideInAnimation,
  modalSlideOutAnimation,
  slideHorizontalAnimation,
  slideVerticalAnimation
} from "@utils/animationUtils";
import { isMobile } from "@utils/platformUtils";

type Props = PropsWithChildren<{
  animated?: "right" | "up";
  desktopPadding?: boolean;
  gradient?: boolean;
  helmetKey: string;
  journeyId: string;
  mobileFullscreen?: boolean;
  mobilePadding?: boolean;
  toolbar?: ReactNode;
}>;

const ClientPage = ({
  animated,
  children,
  desktopPadding = true,
  gradient = true,
  helmetKey,
  journeyId,
  mobileFullscreen = false,
  mobilePadding = true,
  toolbar
}: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();

  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);
  const notificationOpener = useOpener();

  const pageVariants = animated === "right" ? slideHorizontalAnimation("right") : slideVerticalAnimation("up");

  const pageContainerClassNames = [styles.clientPageContainer];
  if (mobileFullscreen) pageContainerClassNames.push(styles.mobileFullscreen);
  if (mobilePadding) pageContainerClassNames.push(styles.mobilePadding);
  if (desktopPadding) pageContainerClassNames.push(styles.desktopPadding);

  const getJourneyForMessagesQuery = useQuery<GetJourneyForMessagesQuery, GetJourneyForMessagesQueryVariables>(
    GetJourneyForMessagesDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  const hasUnreadMessage = useMemo(
    () =>
      !!getJourneyForMessagesQuery.data?.journey.chats.reduce(
        (accumulator, chat) => accumulator + chat.unreadMessageCount,
        0
      ),
    [getJourneyForMessagesQuery.data?.journey.chats]
  );

  const unreadNotifications =
    getJourneyForMessagesQuery.data?.journey.notifications.filter(notification => !notification.readAt).length ?? 0;

  return (
    <AppPageWithMenu
      gradient={gradient}
      hideMenuOnMobile={notificationOpener.isOpen || mobileFullscreen}
      journeyId={journeyId}
    >
      <motion.div
        animate="animate"
        className={gradient ? styles.background : ""}
        exit="exit"
        initial="initial"
        transition={{ duration: 0.5, type: "tween" }}
        variants={animated && isMobile() ? pageVariants : undefined}
      >
        <Helmet>
          <title>{intl.formatMessage({ id: helmetKey })}</title>
        </Helmet>
        <div className={`${mobileFullscreen ? "ion-hide-lg-down" : ""} ${styles.toolbar}`}>
          <IonGrid className="ion-no-padding">
            <IonRow className="ion-align-items-center">
              <IonCol>
                <div className={styles.toolbarLeft}>{toolbar}</div>
              </IonCol>
              <IonCol size="auto">
                <div className="flex gap-md items-center">
                  <div className="ion-hide-lg-up">
                    <ButtonLink className="relative items-center flex" onClick={messagesOpener.toggle} stylized={false}>
                      <div className={styles.button}>
                        <Icon color="primary" icon="message" />
                      </div>
                      {hasUnreadMessage && (
                        <Icon className={styles.hasMessages} color="danger" icon="circleExclamation" />
                      )}
                    </ButtonLink>
                  </div>
                  <ButtonLink
                    className="relative items-center flex"
                    id="notification-button"
                    onClick={notificationOpener.toggle}
                    stylized={false}
                  >
                    <div className={styles.button}>
                      <Icon color="primary" icon="bell" />
                    </div>
                    {unreadNotifications > 0 && (
                      <NumberCircle color="danger" position="top">
                        {unreadNotifications}
                      </NumberCircle>
                    )}
                  </ButtonLink>
                  {myClient && <AccountDropdown journeyId={journeyId} />}
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
        <div className={pageContainerClassNames.join(" ")}>
          <div className={styles.clientPageContent}>{children}</div>
        </div>
        <Modal
          breakpoints={undefined}
          className="ion-hide-lg-up"
          enterAnimation={modalSlideInAnimation}
          fullHeight
          fullscreen
          handleBehavior={undefined}
          hideHeader
          initialBreakpoint={undefined}
          leaveAnimation={modalSlideOutAnimation}
          opener={notificationOpener}
          showBackdrop={false}
        >
          {() => (
            <div>
              <div className={styles.scroll}>
                <MobileHeader
                  handleBack={notificationOpener.close}
                  title={intl.formatMessage({ id: "components.NotificationCenter.heading" })}
                />
                {getJourneyForMessagesQuery.data?.journey && (
                  <div className="ion-margin-start ion-margin-end">
                    <NotificationCenter
                      journey={getJourneyForMessagesQuery.data?.journey}
                      opener={notificationOpener}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      </motion.div>
      <IonPopover
        alignment="center"
        className={`${styles.popover} ion-hide-lg-down roundedPopover`}
        isOpen={notificationOpener.isOpen}
        onIonPopoverDidDismiss={notificationOpener.close}
        onIonPopoverDidPresent={notificationOpener.open}
        onIonPopoverWillPresent={notificationOpener.open}
        side="bottom"
        trigger="notification-button"
      >
        <IonContent>
          <div className="margin-20-top margin-20-bottom margin-20-left margin-20-right padding-30-bottom">
            <div className="text-center padding-30-bottom">
              <Heading2 bold color="black">
                <FormattedMessage id="components.NotificationCenter.heading" />
              </Heading2>
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.close"
                className={styles.closeButton}
                color="gray-300"
                fill="clear"
                icon="xmark"
                onClick={notificationOpener.close}
                size="large"
              />
            </div>
            {getJourneyForMessagesQuery.data?.journey && (
              <NotificationCenter journey={getJourneyForMessagesQuery.data?.journey} opener={notificationOpener} />
            )}
          </div>
        </IonContent>
      </IonPopover>
    </AppPageWithMenu>
  );
};

export default ClientPage;
